import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, PawPrint, Weight, Utensils, Activity, HeartPulse, Pill,
  GraduationCap, Plus, Trash2, Star, Pencil,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

function TrackTab({ dogId, kind, unit, label, icon: Icon, color }) {
  const [logs, setLogs] = useState([]);
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(today());

  const load = useCallback(() => api.get(`/logs/${kind}/${dogId}`).then((r) => setLogs(r.data)), [kind, dogId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!value) { toast.error("Enter a value"); return; }
    await api.post(`/logs/${kind}`, { dog_id: dogId, date, value: parseFloat(value), notes });
    setValue(""); setNotes(""); toast.success(`${label} logged`); load();
  };
  const del = async (id) => { await api.delete(`/logs/${kind}/${id}`); load(); };

  const chartData = [...logs].reverse().map((l) => ({ date: fmt(l.date), value: l.value }));

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Icon className="h-5 w-5" style={{ color }} /><h3 className="font-head font-semibold">{label} trend</h3></div>
        {chartData.length === 0 ? <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">No data yet — add your first entry.</div> : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ left: -18, right: 8, top: 8 }}>
              <defs><linearGradient id={`g-${kind}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={44} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
              <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} fill={`url(#g-${kind})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <h3 className="font-head font-semibold mb-3">Log {label.toLowerCase()}</h3>
        <div className="space-y-3">
          <div><Label>{label} ({unit})</Label><Input data-testid={`${kind}-value`} type="number" value={value} onChange={(e) => setValue(e.target.value)} /></div>
          <div><Label>Date</Label><Input data-testid={`${kind}-date`} type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Notes</Label><Input data-testid={`${kind}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" /></div>
          <Button data-testid={`add-${kind}`} onClick={add} className="w-full rounded-full"><Plus className="h-4 w-4 mr-1" /> Add entry</Button>
        </div>
        <div className="mt-4 space-y-2 max-h-52 overflow-y-auto scroll-thin">
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between text-sm bg-muted/50 rounded-lg px-3 py-2">
              <span><b>{l.value}{unit}</b> · {fmt(l.date)}{l.notes ? ` · ${l.notes}` : ""}</span>
              <button data-testid={`del-${kind}-${l.id}`} onClick={() => del(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HealthTab({ dogId }) {
  const [health, setHealth] = useState([]);
  const [meds, setMeds] = useState([]);
  const [h, setH] = useState({ title: "", symptoms: "", severity: "mild", status: "ongoing" });
  const [m, setM] = useState({ name: "", dosage: "", frequency: "", time_of_day: "", active: true });

  const load = useCallback(() => {
    api.get(`/health/${dogId}`).then((r) => setHealth(r.data));
    api.get(`/medications/${dogId}`).then((r) => setMeds(r.data));
  }, [dogId]);
  useEffect(() => { load(); }, [load]);

  const addHealth = async () => {
    if (!h.title.trim()) { toast.error("Enter a title"); return; }
    await api.post("/health", { ...h, dog_id: dogId, date: today() });
    setH({ title: "", symptoms: "", severity: "mild", status: "ongoing" }); toast.success("Health issue logged"); load();
  };
  const addMed = async () => {
    if (!m.name.trim()) { toast.error("Enter medication name"); return; }
    await api.post("/medications", { ...m, dog_id: dogId, start_date: today() });
    setM({ name: "", dosage: "", frequency: "", time_of_day: "", active: true }); toast.success("Medication added"); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><HeartPulse className="h-5 w-5 text-destructive" /><h3 className="font-head font-semibold">Health problems</h3></div>
        <div className="space-y-2 mb-4">
          <Input data-testid="health-title" placeholder="Issue title (e.g. Limping)" value={h.title} onChange={(e) => setH({ ...h, title: e.target.value })} />
          <Textarea data-testid="health-symptoms" placeholder="Symptoms observed" value={h.symptoms} onChange={(e) => setH({ ...h, symptoms: e.target.value })} />
          <div className="flex gap-2">
            <select data-testid="health-severity" value={h.severity} onChange={(e) => setH({ ...h, severity: e.target.value })} className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option>
            </select>
            <Button data-testid="add-health" onClick={addHealth} className="rounded-full"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto scroll-thin">
          {health.map((it) => (
            <div key={it.id} className="rounded-xl border border-border/60 p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{it.title}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${it.severity === "severe" ? "bg-destructive/15 text-destructive" : it.severity === "moderate" ? "bg-secondary/15 text-secondary" : "bg-primary/15 text-primary"}`}>{it.severity}</span>
                  <button onClick={async () => { await api.delete(`/health/${it.id}`); load(); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              {it.symptoms && <p className="text-sm text-muted-foreground mt-1">{it.symptoms}</p>}
              <Link to={`/research?dog=${dogId}&q=${encodeURIComponent(it.title + ". " + it.symptoms)}`} className="text-xs text-accent-foreground bg-accent/20 px-2 py-1 rounded-full inline-block mt-2 hover:bg-accent/30">🔎 Research this with AI</Link>
            </div>
          ))}
          {health.length === 0 && <p className="text-sm text-muted-foreground">No health issues logged.</p>}
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Pill className="h-5 w-5 text-secondary" /><h3 className="font-head font-semibold">Medication schedule</h3></div>
        <div className="space-y-2 mb-4">
          <Input data-testid="med-name" placeholder="Medication name" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="med-dosage" placeholder="Dosage" value={m.dosage} onChange={(e) => setM({ ...m, dosage: e.target.value })} />
            <Input data-testid="med-frequency" placeholder="Frequency" value={m.frequency} onChange={(e) => setM({ ...m, frequency: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Input data-testid="med-time" placeholder="Time of day (e.g. 8am, 8pm)" value={m.time_of_day} onChange={(e) => setM({ ...m, time_of_day: e.target.value })} />
            <Button data-testid="add-med" onClick={addMed} className="rounded-full"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto scroll-thin">
          {meds.map((it) => (
            <div key={it.id} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
              <div>
                <div className="font-medium">{it.name} <span className="text-sm text-muted-foreground">{it.dosage}</span></div>
                <div className="text-xs text-muted-foreground">{it.frequency} {it.time_of_day ? `· ${it.time_of_day}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={it.active} onCheckedChange={async (v) => { await api.put(`/medications/${it.id}`, { ...it, active: v, dog_id: dogId }); load(); }} />
                <button onClick={async () => { await api.delete(`/medications/${it.id}`); load(); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          {meds.length === 0 && <p className="text-sm text-muted-foreground">No medications scheduled.</p>}
        </div>
      </div>
    </div>
  );
}

function TrainingTab({ dogId }) {
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ skill: "", duration_min: "", rating: 3, notes: "" });
  const load = useCallback(() => api.get(`/training/${dogId}`).then((r) => setLogs(r.data)), [dogId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.skill.trim()) { toast.error("Enter a skill"); return; }
    await api.post("/training", { dog_id: dogId, date: today(), skill: f.skill, duration_min: f.duration_min ? parseFloat(f.duration_min) : null, rating: f.rating, notes: f.notes });
    setF({ skill: "", duration_min: "", rating: 3, notes: "" }); toast.success("Training logged"); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <h3 className="font-head font-semibold mb-3">Log training session</h3>
        <div className="space-y-3">
          <Input data-testid="train-skill" placeholder="Skill (e.g. Recall, Sit)" value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} />
          <Input data-testid="train-duration" type="number" placeholder="Duration (min)" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} />
          <div>
            <Label>Progress rating</Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`rating-${n}`} onClick={() => setF({ ...f, rating: n })}><Star className={`h-6 w-6 ${n <= f.rating ? "fill-secondary text-secondary" : "text-muted-foreground"}`} /></button>
              ))}
            </div>
          </div>
          <Textarea data-testid="train-notes" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          <Button data-testid="add-training" onClick={add} className="w-full rounded-full"><Plus className="h-4 w-4 mr-1" /> Add session</Button>
        </div>
      </div>
      <div className="lg:col-span-2 rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><GraduationCap className="h-5 w-5 text-primary" /><h3 className="font-head font-semibold">Progress reports</h3></div>
        <div className="space-y-2 max-h-[26rem] overflow-y-auto scroll-thin">
          {logs.map((l) => (
            <div key={l.id} className="rounded-xl border border-border/60 p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{l.skill}</span>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= (l.rating || 0) ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />)}
                  <button onClick={async () => { await api.delete(`/training/${l.id}`); load(); }} className="ml-2 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{fmt(l.date)} {l.duration_min ? `· ${l.duration_min} min` : ""}</div>
              {l.notes && <p className="text-sm text-muted-foreground mt-1">{l.notes}</p>}
            </div>
          ))}
          {logs.length === 0 && <p className="text-sm text-muted-foreground">No training sessions yet.</p>}
        </div>
      </div>
    </div>
  );
}

const fields = ["name", "breed", "sex", "birthdate", "color", "photo_url", "pack_role", "notes"];

function EditDogDialog({ dog, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fields.reduce((a, k) => ({ ...a, [k]: dog[k] || "" }), {}));
  }, [open, dog]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Please enter a name"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/dogs/${dog.id}`, form);
      toast.success("Profile updated");
      onSaved(r.data); onOpenChange(false);
    } catch { toast.error("Could not save changes"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head">Edit {dog.name}</DialogTitle>
          <DialogDescription>Update your dog's profile details and save.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto scroll-thin pr-1">
          <div className="col-span-2"><Label>Name *</Label><Input data-testid="edit-dog-name" value={form.name || ""} onChange={set("name")} placeholder="Rex" /></div>
          <div><Label>Breed</Label><Input data-testid="edit-dog-breed" value={form.breed || ""} onChange={set("breed")} placeholder="Labrador" /></div>
          <div><Label>Sex</Label><Input data-testid="edit-dog-sex" value={form.sex || ""} onChange={set("sex")} placeholder="Male / Female" /></div>
          <div><Label>Birthdate</Label><Input data-testid="edit-dog-birthdate" type="date" value={form.birthdate || ""} onChange={set("birthdate")} /></div>
          <div><Label>Color</Label><Input data-testid="edit-dog-color" value={form.color || ""} onChange={set("color")} placeholder="Golden" /></div>
          <div className="col-span-2"><Label>Pack role</Label><Input data-testid="edit-dog-role" value={form.pack_role || ""} onChange={set("pack_role")} placeholder="e.g. Leader, Follower" /></div>
          <div className="col-span-2"><Label>Photo URL</Label><Input data-testid="edit-dog-photo" value={form.photo_url || ""} onChange={set("photo_url")} placeholder="https://..." /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="edit-dog-notes" value={form.notes || ""} onChange={set("notes")} placeholder="Personality, allergies..." /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-edit-dog" onClick={submit} disabled={saving} className="rounded-full">{saving ? "Saving..." : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dog, setDog] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => { api.get(`/dogs/${id}`).then((r) => setDog(r.data)); }, [id]);

  const removeDog = async () => {
    try {
      await api.delete(`/dogs/${id}`);
      toast.success(`${dog.name} removed from your pack`);
      navigate("/dogs");
    } catch { toast.error("Could not delete dog"); }
  };

  if (!dog) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="dog-detail-page">
      <Link to="/dogs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back to dogs</Link>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-6">
        <div className="h-20 w-20 rounded-2xl overflow-hidden bg-muted shrink-0">
          {dog.photo_url ? <img src={dog.photo_url} alt={dog.name} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><PawPrint className="h-8 w-8 text-muted-foreground" /></div>}
        </div>
        <div className="flex-1">
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{dog.name}</h1>
          <p className="text-muted-foreground">{dog.breed || "Mixed breed"} {dog.sex ? `· ${dog.sex}` : ""} {dog.pack_role ? `· ${dog.pack_role}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="edit-dog-btn" variant="outline" onClick={() => setEditOpen(true)} className="rounded-full">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-dog-btn" variant="outline" className="rounded-full text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Delete</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {dog.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes {dog.name} and all their logged weight, food, exercise, health, medication, training and behaviour data. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-dog">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete-dog" onClick={removeDog} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </motion.div>

      <EditDogDialog dog={dog} open={editOpen} onOpenChange={setEditOpen} onSaved={setDog} />

      <Tabs defaultValue="weight">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/60 rounded-xl p-1">
          <TabsTrigger value="weight" data-testid="tab-weight" className="rounded-lg"><Weight className="h-4 w-4 mr-1.5" />Weight</TabsTrigger>
          <TabsTrigger value="food" data-testid="tab-food" className="rounded-lg"><Utensils className="h-4 w-4 mr-1.5" />Food</TabsTrigger>
          <TabsTrigger value="exercise" data-testid="tab-exercise" className="rounded-lg"><Activity className="h-4 w-4 mr-1.5" />Exercise</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health" className="rounded-lg"><HeartPulse className="h-4 w-4 mr-1.5" />Health & Meds</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training" className="rounded-lg"><GraduationCap className="h-4 w-4 mr-1.5" />Training</TabsTrigger>
        </TabsList>
        <div className="mt-6">
          <TabsContent value="weight"><TrackTab dogId={id} kind="weight" unit="kg" label="Weight" icon={Weight} color="hsl(var(--chart-1))" /></TabsContent>
          <TabsContent value="food"><TrackTab dogId={id} kind="food" unit="g" label="Food" icon={Utensils} color="hsl(var(--chart-2))" /></TabsContent>
          <TabsContent value="exercise"><TrackTab dogId={id} kind="exercise" unit="min" label="Exercise" icon={Activity} color="hsl(var(--chart-3))" /></TabsContent>
          <TabsContent value="health"><HealthTab dogId={id} /></TabsContent>
          <TabsContent value="training"><TrainingTab dogId={id} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
